/**
 * Customer Trial List Hook
 * Also used for Cancelled list with different API
 */
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    getCustomersTrial,
    getCustomersCancelled,
} from '@/modules/Customer/services'
import {
    getTrialCustomerColumns,
    getCancelledCustomerColumns,
    getTrialServiceColumns,
    getTrialFilterItems,
    TRIAL_FILTER_DEFAULTS,
    TRIAL_SORT_CONFIG,
    getCustomerServices,
    hasServices,
} from '../utils/customer-trial-config'

interface TrialFilters {
    customer_sale_followers: number[]
    customer_cs_followers: number[]
    customer_industry: string[]
    product_group_name: string[]
    customer_source: string[]
    customer_source_type: string[]
}

type ListType = 'trial' | 'cancelled'

export const useCustomerTrialList = (type: ListType = 'trial') => {
    const { t } = useTranslation('customer')

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<TrialFilters>({
        defaults: {
            page: 1,
            page_size: 20,
            ...TRIAL_SORT_CONFIG.defaultSort,
        },
        filterDefaults: TRIAL_FILTER_DEFAULTS,
    })

    const { control } = useForm<TrialFilters>({
        defaultValues: filters,
        values: filters,
    })

    const apiCall = type === 'trial' ? getCustomersTrial : getCustomersCancelled

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, type, queryParams],
        queryFn: () =>
            apiCall({
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                sort_by: params.sort_by,
                sort_order: params.sort_order,
                filters: queryParams.filters,
            }),
        placeholderData: true,
    })

    const customers = data?.results || []
    const pagination = data?.pagination

    const columns = useMemo(
        () =>
            type === 'cancelled'
                ? getCancelledCustomerColumns(t)
                : getTrialCustomerColumns(t),
        [t, type]
    )
    const serviceColumns = useMemo(() => getTrialServiceColumns(t), [t])
    const filterItems = useMemo(() => getTrialFilterItems(t), [t])

    // Pagination handlers
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )

    // Sort handler
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    return {
        customers,
        pagination,
        stateComponent: renderState(),
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        serviceColumns,
        filterItems,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        getCustomerServices,
        hasServices,
        t,
    }
}
